import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { usePatchingStore, CableData } from '../../stores/usePatchingStore';
import { PortGenerator } from './PortGenerator';

interface PortData {
  id: string;
  name: string;
  port_type: string;
  connector_type?: string;
}

export function PatchPanelMatrix({ equipmentId, isLeftPanel }: { equipmentId: string, isLeftPanel: boolean }) {
  const [ports, setPorts] = useState<PortData[]>([]);
  const [portStatuses, setPortStatuses] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const store = usePatchingStore();
  const selectedPorts = isLeftPanel ? store.selectedLeftPorts : store.selectedRightPorts;

  const fetchPorts = async () => {
    setLoading(true);
    try {
      const [portsRes, cablesRes] = await Promise.all([
        api.get(`/api/equipment/${equipmentId}/ports`),
        api.get(`/api/equipment/${equipmentId}/cables`)
      ]);
      setPorts(portsRes.data);

      // [D2] Сохраняем данные о кабелях в стор (для Commit All)
      const cables: CableData[] = cablesRes.data;
      if (isLeftPanel) store.setLeftCables(cables);
      else store.setRightCables(cables);

      const statuses = new Map<string, string>();
      cables.forEach((c: CableData) => {
        statuses.set(c.port_a_id, c.status || 'Live');
        statuses.set(c.port_b_id, c.status || 'Live');
      });
      setPortStatuses(statuses);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPorts();
  }, [equipmentId, store.patchingTrigger]);

  if (loading) return <div className="p-4 text-gray-500">Loading ports...</div>;

  if (ports.length === 0) {
    return <PortGenerator equipmentId={equipmentId} onPortsGenerated={fetchPorts} />;
  }

  const handlePortClick = (e: React.MouseEvent, portId: string) => {
    if (e.shiftKey) {
      // Bulk select logic
      const lastSelected = selectedPorts[selectedPorts.length - 1];
      if (lastSelected) {
        const lastIdx = ports.findIndex(p => p.id === lastSelected);
        const currIdx = ports.findIndex(p => p.id === portId);
        if (lastIdx !== -1 && currIdx !== -1) {
          const start = Math.min(lastIdx, currIdx);
          const end = Math.max(lastIdx, currIdx);
          const newSelection = ports.slice(start, end + 1).map(p => p.id);
          if (isLeftPanel) store.setLeftPorts(newSelection);
          else store.setRightPorts(newSelection);
          return;
        }
      }
    }
    
    if (isLeftPanel) store.toggleLeftPort(portId);
    else store.toggleRightPort(portId);
  };

  return (
    <div className="bg-gray-800 border border-gray-700 p-4 rounded shadow-lg max-h-[60vh] overflow-auto">
      <div className="mb-4 flex justify-between items-center border-b border-gray-700 pb-2">
        <h3 className="font-bold text-white text-lg">Ports ({ports.length})</h3>
        <div className="text-xs text-gray-400">
          Selected: <span className="text-blue-400 font-bold">{selectedPorts.length}</span>
        </div>
      </div>
      
      {/* Dynamic grid layout to match standard 24/48 switches */}
      <div className="grid grid-cols-12 gap-1.5">
        {ports.map((port) => {
          const isSelected = selectedPorts.includes(port.id);
          const pStatus = portStatuses.get(port.id);
          // Show just numbers if possible, otherwise short text
          const num = port.name.replace(/[^0-9]/g, '');
          const label = num ? num : port.name.substring(0, 3);
          
          let bgClass = 'bg-gray-800 border-gray-600 hover:border-gray-400 hover:bg-gray-700';
          if (isSelected) {
            bgClass = 'bg-blue-600 border-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.5)]';
          } else if (pStatus === 'Live') {
            bgClass = 'bg-green-700 border-green-500 hover:bg-green-600';
          } else if (pStatus === 'Planned') {
            bgClass = 'bg-orange-700 border-orange-500 hover:bg-orange-600';
          } else if (pStatus === 'Faulted') {
            bgClass = 'bg-red-700 border-red-500 hover:bg-red-600';
          }
          
          return (
            <button
              key={port.id}
              onClick={(e) => handlePortClick(e, port.id)}
              className={`
                h-10 w-10 flex flex-col items-center justify-center rounded border transition-all relative
                ${bgClass}
              `}
              title={`${port.name} (${port.port_type})`}
            >
              <div className="w-5 h-2.5 bg-black opacity-40 border-b-2 border-gray-900 rounded-sm mb-1"></div>
              <span className="text-[10px] font-mono text-white font-bold">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
